import { bcSessionKeys } from './../common/constants/session-keys';
import { config as defaultConfig } from './../../environments/environment';
import { Injectable } from '@angular/core';
import { AuthenticationModes } from 'common/config/models/authentication-modes';
import { Config } from 'environments/models/config.model';

/**
 * Helper class to read application configuration.
 */
@Injectable()
export class BcConfigService {
  private _config: Config;
  private _restApiHost: string;
  private _webSocketEndpoint: string;
  private _fileUploadHost: string;
  private _serverSetDomain: string;

  /**
   * Creates a new service instance to manage the current configuration values.
   */
  constructor() {
    this.setCurrentConfig(defaultConfig);
  }

  /**
   * Returns the currently used configuration settings.
   * @returns {object} The full configuration object.
   */
  get current(): Config {
    return this._config;
  }

  get serverSetDomain(): string {
    return this._serverSetDomain;
  }

  get authenticationMode(): AuthenticationModes {
    return AuthenticationModes[this._config.authenticationMode] || AuthenticationModes.BOLD360;
  }

  setServerSetDomain(value) {
    this._serverSetDomain = value;
  }

  setCurrentConfig(config: Config): void {
    this._config = { ...config };
    this._deepFreeze(this._config);

    if (!this.current.restApiHost && this.current.environment !== 'dx') {
      throw new Error('Please specify rest api host url in the config!');
    }

    if (!this.current.features.geoAffinity) {
      this.setWebSocketEndpointFromLocalConfig();
      this.setRestApiHostFromLocalConfig();
      this.setFileUploadHostFromLocalConfig();
    } else {
      const endpoints = this.getEndpointsFromLocalStorage() || this.current;
      this.setRestApiUrl(endpoints.restApiHost);
      this.setFileUploadHost(endpoints.fileUploadHost);
    }
  }

  getEndpointsFromLocalStorage() {
    const restApiHost = sessionStorage.getItem(bcSessionKeys.storage.restApiHost);
    const fileUploadHost = sessionStorage.getItem(bcSessionKeys.storage.fileUploadHost);

    return (restApiHost && fileUploadHost) ? {
      restApiHost,
      fileUploadHost
    } : null;
  }

  setRestApiHostFromLocalConfig() {
    this.setRestApiUrl(this.current.restApiHost);
  }

  setRestApiUrl(restApiHost) {
    sessionStorage.setItem(bcSessionKeys.storage.restApiHost, restApiHost);
    this._restApiHost = restApiHost;
  }

  getRestApiHost(): string {
    return this._restApiHost;
  }

  setWebSocketEndpointFromLocalConfig() {
    this.setWebSocketEndpoint(this.current.webSocketEndpoint);
  }

  setWebSocketEndpoint(webSocketEndpoint) {
    this._webSocketEndpoint = webSocketEndpoint;
  }

  getWebSocketEndpoint(): string {
    return this._webSocketEndpoint;
  }

  setFileUploadHostFromLocalConfig() {
    this.setFileUploadHost(this.current.fileUploadHost);
  }

  setFileUploadHost(fileUploadHost) {
    sessionStorage.setItem(bcSessionKeys.storage.fileUploadHost, fileUploadHost);
    this._fileUploadHost = fileUploadHost;
  }

  getFileUploadHost(): string {
    return this._fileUploadHost;
  }

  isFeatureSwitchOn(featureName) {
    return Boolean(this._config && this._config.features && this._config.features[featureName]);
  }

  isGenesysConversationEnabled(): boolean {
    return this.isGenesysMessageConversationEnabled() || this.isGenesysEmailConversationEnabled();
  }

  isGenesysMessageConversationEnabled(): boolean {
    return this.current.features.genesysDxChat;
  }

  isGenesysEmailConversationEnabled(): boolean {
    return this.current.features.genesysDxEmail;
  }

  constructAssetUrl(assetUrl: string) {
    return `${this.current.assetPrefix}${assetUrl}`;
  }

  /**
   * Makes the specified object immutable by making it recursively frozen.
   * @param {object} obj The object to freeze.
   * @private
   */
  _deepFreeze(obj) {
    // Retrieve the property names defined on the specified object.
    const propNames = Object.getOwnPropertyNames(obj);

    // Freeze properties before freezing self.
    propNames.forEach((name) => {
      const prop = obj[name];

      // Freeze property only if it is an object.
      if (typeof prop === 'object' && prop !== null) {
        this._deepFreeze(prop);
      }
    });

    // Freeze self.
    return Object.freeze(obj);
  }
}

export const configServiceInstance = new BcConfigService();

export function ConfigProvider() {
  return {
    $get: () => configServiceInstance
  };
}
